import { axios, base } from '../http';

/**
 * 校车自动考勤与班主任排班 API 模块。创建于 2026-09-08
 */

// =================== 1. 学校校车配置 ===================

/**
 * 获取学校校车自动离校考勤配置
 * @param {Object} params - { schoolDepartmentId }
 */
export const getSchoolBusConfig = (params) => {
  return axios.get(`${base}/api/SchoolBusConfig/GetConfig`, { params });
};

/**
 * 保存学校校车自动离校考勤配置
 * @param {Object} data - { schoolDepartmentId, enabled, intervalMinutes, defaultRideTime, toleranceMinutes, skipWeekends }
 */
export const saveSchoolBusConfig = (data) => {
  return axios.post(`${base}/api/SchoolBusConfig/SaveConfig`, data);
};

// =================== 2. 班主任移动端 H5 排班 ===================

/**
 * 获取当前教师负责的班主任班级列表
 * @param {Object} params - { teacherInfoId }
 */
export const getMyHomeroomClasses = (params) => {
  return axios.get(`${base}/api/h5/SchoolBusClassScheduleH5/GetMyClasses`, { params });
};

/**
 * 获取指定班级某一整周（周一至周日共7天）排班状态卡片
 * @param {Object} params - { classDepartmentId, startDate } - startDate 可选，周内任意日期或周一（yyyy-MM-dd），不传默认本周
 */
export const getScheduleWeekDayCards = (params) => {
  return axios.get(`${base}/api/h5/SchoolBusClassScheduleH5/GetWeekDayCards`, { params });
};

/**
 * 获取班级某日排班及全班学生排班设置
 * @param {Object} params - { classDepartmentId, scheduleDate }
 */
export const getClassSchedule = (params) => {
  return axios.get(`${base}/api/h5/SchoolBusClassScheduleH5/GetClassSchedule`, { params });
};

/**
 * 保存班级某日乘车排班
 * @param {Object} data - { classDepartmentId, scheduleDate, unifiedRideTimes, teacherInfoId, teacherName, students: [{ studentId, rideTimes }] }
 */
export const saveClassSchedule = (data) => {
  return axios.post(`${base}/api/h5/SchoolBusClassScheduleH5/SaveClassSchedule`, data);
};

/**
 * 复制排班到目标日期
 * @param {Object} data - { classDepartmentId, sourceDate, targetDates }
 */
export const copyScheduleToDates = (data) => {
  return axios.post(`${base}/api/h5/SchoolBusClassScheduleH5/CopyScheduleToDates`, data);
};

// =================== 3. 汇总报表与导出 ===================

/**
 * 获取乘车总览数据
 * @param {Object} params - { schoolDepartmentId, startDate, endDate, classDepartmentId, source }
 */
export const getSchoolBusOverview = (params) => {
  return axios.get(`${base}/api/SchoolBusSummary/GetOverview`, { params });
};

/**
 * 学生月度乘车次数汇总
 * @param {Object} params - { schoolDepartmentId, month, classDepartmentId, keyword, source, page, pageSize }
 */
export const getStudentMonthSummary = (params) => {
  return axios.get(`${base}/api/SchoolBusSummary/GetStudentMonthSummary`, { params });
};

/**
 * 每日学生乘车汇总
 * @param {Object} params - { schoolDepartmentId, startDate, endDate, classDepartmentId, studentId, source, page, pageSize }
 */
export const getStudentDailySummary = (params) => {
  return axios.get(`${base}/api/SchoolBusSummary/GetStudentDailySummary`, { params });
};

/**
 * 每日班级乘车汇总
 * @param {Object} params - { schoolDepartmentId, startDate, endDate, classDepartmentId, page, pageSize }
 */
export const getClassDailySummary = (params) => {
  return axios.get(`${base}/api/SchoolBusSummary/GetClassDailySummary`, { params });
};

/**
 * 查看学生某日乘车明细流水
 * @param {Object} params - { studentId, date, startDate, endDate, source }
 */
export const getDepartureDetails = (params) => {
  return axios.get(`${base}/api/SchoolBusSummary/GetDepartureDetails`, { params });
};

// =================== 4. 后台只读排班记录查询 ===================

/**
 * 分页查询班级排班记录列表（后台管理员只读查看）
 * @param {Object} params - { schoolDepartmentId, startDate, endDate, classDepartmentId, status, page, pageSize }
 * @returns {Promise<Object>} 直接返回后端 MessageModel（已解开 axios 的 data 层）
 */
export const getClassScheduleRecordList = (params) => {
  return axios.get(`${base}/api/SchoolBusSummary/GetClassScheduleRecordList`, { params }).then((res) => res.data);
};

/**
 * 查看某次排班的学生名单与推送状态明细
 * @param {Object} params - { scheduleId }
 * @returns {Promise<Object>} 直接返回后端 MessageModel（已解开 axios 的 data 层）
 */
export const getScheduleStudentDetails = (params) => {
  return axios.get(`${base}/api/SchoolBusSummary/GetScheduleStudentDetails`, { params }).then((res) => res.data);
};

/**
 * 导出班级排班记录 Excel（blob，自动携带 Authorization）
 * @param {Object} params - { schoolDepartmentId, startDate, endDate, classDepartmentId, status }
 */
export const exportClassScheduleRecordList = (params) => {
  return axios.get(`${base}/api/SchoolBusSummary/ExportClassScheduleRecordList`, {
    params,
    responseType: 'blob'
  });
};

/**
 * 导出 Excel 文件 URL（备用）
 */
export const getExportSummaryUrl = (params) => {
  const query = Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== '')
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');
  return `${base}/api/SchoolBusSummary/ExportSummary?${query}`;
};

/**
 * 导出乘车汇总 Excel (二进制流方式，自动携带 Authorization 请求头)
 * @param {Object} params - { schoolDepartmentId, type, month, startDate, endDate, classDepartmentId, source }
 */
export const exportSchoolBusSummary = (params) => {
  return axios.get(`${base}/api/SchoolBusSummary/ExportSummary`, { params, responseType: 'blob' });
};
